import { useCallback, useEffect, useMemo, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import {
  shopModeApi,
  type FloorBouquetInStock,
  type FloorSellableItem,
  type ShopFloorCreateSalePayload,
} from "../../api/shopMode";
import type { BelowFloorFlag } from "../../api/types";
import {
  NO_PAYMENT_CHOICE,
  readPaymentChoice,
  type PaymentChoice,
} from "../../utils/paymentSplit";
import { ShopFloorPaymentSplit, paymentProblemText } from "./ShopFloorPaymentSplit";
import { eur } from "./shopFloorFormat";
import { BelowFloorPrompt } from "./BelowFloorPrompt";
import { useBoxPickPrompt } from "./useBoxPickPrompt";
import { SaleCompleteFlow } from "./SaleCompleteFlow";
import { StockItemFinder } from "./StockItemFinder";
import { ShopFloorTicketLines } from "./ShopFloorTicketLines";
import {
  cartBouquetFloorLines,
  cartToSaleLines,
  ticketLineProblem,
  useTicketCart,
} from "./ticketCart";
import { ShopFloorScheduleBlock } from "./ShopFloorScheduleBlock";
import {
  emptyScheduleDraft,
  scheduleToOrderPayload,
  validateSchedule,
  type ScheduleDraft,
} from "./scheduleDraft";
import { ShopFloorIcon } from "./ShopFloorIcon";
import { belowFloorWarnings } from "./kioskCostDisplay";
import { useT } from "../../i18n/LanguageProvider";
import { dateText, money } from "../../i18n/intl";
import { orderDayDate } from "../orders/orderUi";


/**
 * Shop-floor register. Manual product search and barcode scanning deliberately
 * converge on the same item-to-ticket function; checkout remains the existing
 * floor sale endpoint and therefore keeps its FIFO, price and commission rules.
 */
export function ShopFloorSalePanel({
  onSold,
}: {
  onSold?: () => void;
}) {
  const { t } = useT();
  const [bouquets, setBouquets] = useState<FloorBouquetInStock[]>([]);
  const [items, setItems] = useState<FloorSellableItem[]>([]);
  const [loadingStock, setLoadingStock] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // G-141 (OD-215): Cash, Card, or both with the cash part typed.
  const [payment, setPayment] = useState<PaymentChoice>(NO_PAYMENT_CHOICE);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // G-112 (OD-94): "Schedule order" turns the ticket into an ORDER (never a
  // sale) — it replaced WP-C's "Send with courier", which rang the sale up.
  const [schedule, setSchedule] = useState<ScheduleDraft>(emptyScheduleDraft);
  const [scheduled, setScheduled] = useState<{ code: string; day: Date } | null>(
    null,
  );
  // G-45 part 3: lines the sale flagged below floor — prompt the florist.
  const [belowFloorFlags, setBelowFloorFlags] = useState<BelowFloorFlag[]>([]);
  // G-84: "Is this from box X?" — asked by the server, answered here, resent.
  const boxPrompt = useBoxPickPrompt();
  // G-156: the whole post-sale sequence (receipt question → earnings) runs
  // off this; the below-floor prompt follows it, so its flags wait here too.
  const [success, setSuccess] = useState<{
    saleId: string;
    commissionPercent: string | null;
    commissionEarnedEur: string;
    summary: string;
    belowFloor: BelowFloorFlag[];
  } | null>(null);
  const clearLastResult = useCallback(() => {
    setSuccess(null);
    setScheduled(null);
    setSubmitError(null);
  }, []);
  // G-147: the ticket is shared with "Customer collected"'s "+ Add items".
  const ticket = useTicketCart(clearLastResult);
  const { cart, setCart, totalCents, cartedBuildIds, itemQtyInCart } = ticket;
  const loadSaleStock = useCallback(async () => {
    setLoadingStock(true);
    setLoadError(null);
    try {
      const [nextBouquets, nextItems] = await Promise.all([
        shopModeApi.floorListBouquetsInStock(),
        shopModeApi.floorListSellableItemsInStock(),
      ]);
      setBouquets(nextBouquets);
      setItems(nextItems);
    } catch (error) {
      // Call-time `translate`, not the hook's `t`: a language switch must not
      // refetch the stock through this callback's dependencies.
      setLoadError(errorText(error, "sale.couldNotLoadStock"));
    } finally {
      setLoadingStock(false);
    }
  }, []);

  useEffect(() => {
    void loadSaleStock();
  }, [loadSaleStock]);

  const total = totalCents / 100;
  const paymentReading = useMemo(
    () => readPaymentChoice(payment, totalCents),
    [payment, totalCents],
  );
  // ShopFloorPaymentSplit shows this one itself, under the two boxes.
  const splitInlineMessage =
    payment.cash && payment.card && !paymentReading.ok
      ? paymentProblemText(paymentReading.problem, totalCents, t)
      : null;

  const validationMessage = useMemo(() => {
    if (cart.length === 0) return t("sale.addSomething");
    const lineProblem = ticketLineProblem(cart, t);
    if (lineProblem) return lineProblem;
    if (schedule.enabled) return validateSchedule(schedule, total);
    if (!paymentReading.ok) {
      return paymentProblemText(paymentReading.problem, totalCents, t);
    }
    return null;
  }, [cart, paymentReading, schedule, total, totalCents, t]);

  /**
   * G-112: the ticket as a scheduled ORDER — nothing is sold or consumed.
   * Items become RETAIL_ITEM lines, shelf bouquets READY_BOUQUET lines (held
   * for the order, OD-98); the sale happens at the handover (OD-94).
   */
  const submitSchedule = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const order = await shopModeApi.floorCreateOrder(
        scheduleToOrderPayload(
          schedule,
          total,
          cart.map((line) =>
            line.kind === "BOUQUET"
              ? {
                  role: "READY_BOUQUET",
                  description: line.label,
                  bouquetBuildId: line.bouquetBuildId,
                  quantity: 1,
                  unitPriceEur: Number(line.priceStr),
                }
              : {
                  role: "RETAIL_ITEM",
                  description: line.label,
                  itemId: line.itemId,
                  quantity: Number(line.qtyStr),
                  unitPriceEur: Number(line.priceStr),
                },
          ),
        ),
      );
      setScheduled({ code: order.code, day: orderDayDate(order) });
      setCart([]);
      setPayment(NO_PAYMENT_CHOICE);
      setSchedule(emptyScheduleDraft());
      // The held bouquets now read "for order …" in the picker.
      await loadSaleStock();
    } catch (error) {
      setSubmitError(errorText(error, "schedule.couldNotSchedule"));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    if (validationMessage) return;
    if (schedule.enabled) {
      await submitSchedule();
      return;
    }
    if (!paymentReading.ok) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const lines = cartToSaleLines(cart);
      const payload: ShopFloorCreateSalePayload = {
        payments: paymentReading.parts,
        lines,
      };
      const result = await boxPrompt.run((boxAnswers) =>
        shopModeApi.floorCreateSale(
          boxAnswers.length ? { ...payload, boxAnswers } : payload,
        ),
      );
      // G-156 (OD-34): receipt question first, then the earnings card with
      // its sound and 2.5 s dismissal (OD-18), then the below-floor prompt.
      setSuccess({
        saleId: result.id,
        commissionPercent: result.commissionPercent,
        commissionEarnedEur: result.commissionEarnedEur,
        summary: `${money(Number(result.totalEur))} · ${t("sale.lines", { count: result.lineCount })}`,
        belowFloor: result.belowFloor ?? [],
      });
      setCart([]);
      setPayment(NO_PAYMENT_CHOICE);
      await loadSaleStock();
      onSold?.();
    } catch (error) {
      setSubmitError(errorText(error, "sale.couldNotLog"));
    } finally {
      setSubmitting(false);
    }
  };

  const availableBouquets = bouquets.filter((bouquet) => !cartedBuildIds.has(bouquet.id));

  // G-45 part 3: warn BEFORE the sale, naming the floor — never blocking.
  // WO-B (OD-198): a bouquet whose cost is incomplete has no floor to warn
  // against; its ticket line says "cannot check" instead (kioskCostDisplay).
  const floorWarnings = belowFloorWarnings(cartBouquetFloorLines(cart));

  return (
    <section className="card shop-floor-sale-panel">
      {success ? (
        <SaleCompleteFlow
          saleId={success.saleId}
          commissionPercent={success.commissionPercent}
          commissionEarnedEur={success.commissionEarnedEur}
          summary={success.summary}
          onDone={() => {
            setBelowFloorFlags(success.belowFloor);
            setSuccess(null);
          }}
        />
      ) : null}

      {belowFloorFlags.length > 0 ? (
        <BelowFloorPrompt
          flags={belowFloorFlags}
          onDone={() => setBelowFloorFlags([])}
        />
      ) : null}
      {boxPrompt.element}

      <div className="shop-floor-sale-layout">
        <div className="shop-floor-sale-catalogue">
          {/* The header and load error live in the catalogue column, not
              above the layout, so the ticket starts at the top of the
              workspace and its viewport-bounded height (base.css) holds the
              submit on screen. */}
          <header className="shop-floor-sale-header">
            <div>
              <h2>{t("sale.title")}</h2>
              <p className="muted">{t("sale.intro")}</p>
            </div>
          </header>

          {loadError ? <div className="alert error">{loadError}</div> : null}

          <section className="shop-floor-sale-bouquets" aria-labelledby="sale-bouquets-heading">
            <div className="shop-floor-sale-section-heading">
              <div>
                <h3 id="sale-bouquets-heading">{t("hub.bouquetsInStock")}</h3>
                <p className="muted">{t("sale.tapBouquet")}</p>
              </div>
              <span className="shop-floor-sale-count">{availableBouquets.length}</span>
            </div>
            {availableBouquets.length === 0 ? (
              <div className="shop-floor-sale-inline-empty">
                {loadingStock
                  ? t("sale.loadingBouquets")
                  : bouquets.length === 0
                    ? t("sale.noBouquets")
                    : t("sale.allBouquetsInTicket")}
              </div>
            ) : (
              <div className="shop-floor-sale-bouquet-list">
                {availableBouquets.map((bouquet) => {
                  // OD-31: a build made FOR an order is listed (it is real
                  // stock on the bench) but never walk-in sellable — the
                  // server refuses it too; this just says why up front.
                  const spokenFor =
                    bouquet.forOrderCode != null || bouquet.forShopify;
                  return (
                    <button
                      key={bouquet.id}
                      type="button"
                      className="shop-floor-sale-bouquet-card"
                      onClick={() =>
                        ticket.addBouquet(
                          bouquet,
                          bouquet.name || bouquet.templateName || t("sale.bouquet"),
                        )
                      }
                      disabled={spokenFor}
                    >
                      <span className="shop-floor-sale-bouquet-plus" aria-hidden="true">+</span>
                      <span>
                        <strong>{bouquet.name || bouquet.templateName || t("sale.bouquet")}</strong>
                        <small>
                          {spokenFor
                            ? bouquet.forOrderCode != null
                              ? t("sale.forOrder", { code: bouquet.forOrderCode })
                              : t("sale.forShopify")
                            : bouquet.suggestedSalePriceEur != null
                              ? t("sale.suggestedPrice", {
                                  price: eur(Number(bouquet.suggestedSalePriceEur)),
                                })
                              : t("sale.priceNeeded")}
                        </small>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="shop-floor-sale-products" aria-labelledby="sale-products-heading">
            <div className="shop-floor-sale-section-heading">
              <div>
                <h3 id="sale-products-heading">{t("sale.products")}</h3>
                <p className="muted">{t("sale.productsHint")}</p>
              </div>
              <span className="shop-floor-sale-count">{items.length}</span>
            </div>

            <StockItemFinder
              items={items}
              loading={loadingStock}
              qtyInTicket={itemQtyInCart}
              onPick={ticket.addItem}
            />
          </section>
        </div>

        <aside className="shop-floor-sale-ticket" aria-labelledby="sale-ticket-heading">
          <div className="shop-floor-sale-ticket-heading">
            <div>
              <h3 id="sale-ticket-heading">{t("sale.ticket")}</h3>
              <p className="muted">
                {cart.length === 0
                  ? t("sale.nothingAdded")
                  : t("sale.lines", { count: cart.length })}
              </p>
            </div>
            <strong>{eur(total)}</strong>
          </div>

          <div className="shop-floor-sale-ticket-lines">
            {cart.length === 0 ? (
              <div className="shop-floor-sale-ticket-empty">
                <span aria-hidden="true"><ShopFloorIcon name="add-sale" size={28} /></span>
                <strong>{t("sale.ticketEmpty")}</strong>
                <p>{t("sale.ticketEmptyHint")}</p>
              </div>
            ) : (
              <ShopFloorTicketLines ticket={ticket} />
            )}
          </div>

          <div className="shop-floor-sale-checkout">
            {floorWarnings.length > 0 ? (
              <div className="alert warning below-floor-warning">
                {floorWarnings.map((w) => (
                  <div key={w}>{w}</div>
                ))}
              </div>
            ) : null}
            {/* A scheduled order records its payment in the block below. */}
            {!schedule.enabled ? (
              <ShopFloorPaymentSplit
                value={payment}
                onChange={setPayment}
                totalCents={totalCents}
                idPrefix="sale"
                disabled={submitting}
              />
            ) : null}

            <div className="shop-floor-sale-grand-total">
              <span>{t("sale.total")}</span>
              <strong>{eur(total)}</strong>
            </div>

            <ShopFloorScheduleBlock
              value={schedule}
              onChange={setSchedule}
              goodsTotal={total}
              disabled={submitting}
            />

            {scheduled ? (
              <div className="alert success" role="status">
                {t("schedule.done", {
                  code: scheduled.code,
                  day: dateText(scheduled.day, {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  }),
                })}
              </div>
            ) : null}

            {submitError ? <div className="alert error">{submitError}</div> : null}
            {/* A split's own problem is already said under its two boxes. */}
            {validationMessage && cart.length > 0 && validationMessage !== splitInlineMessage ? (
              <p className="shop-floor-sale-validation">{validationMessage}</p>
            ) : null}

            <button
              type="button"
              className="button shop-floor-sale-submit"
              onClick={() => void submit()}
              disabled={submitting || validationMessage !== null}
            >
              <ShopFloorIcon name="check" size={20} />
              {submitting
                ? schedule.enabled
                  ? t("sale.scheduling")
                  : t("sale.logging")
                : schedule.enabled
                  ? t("sale.scheduleOrder")
                  : t("sale.log")}
            </button>
          </div>
        </aside>
      </div>
    </section>
  );
}
